// Client Dashboard theme layout data — SINGLE layout, no picker/dropdown,
// unlike navbar/footer/hero (which are real content admins browse/pick
// across brands). This component is pure chrome/theme config: each brand's
// admin should see ONLY its own colors, automatically, the moment it loads —
// no layout choice to make, no manual "Reset to defaults" step required.
//
// The one layout's default colors are picked automatically from
// REACT_APP_PROJECT_CODE (via BRAND_DEFAULT_PROPS_BY_CODE below), computed
// once at module load — this file has no dependency on componentRegistry.js's
// layout-picker/color-translation machinery at all (that machinery is for
// components with real per-brand CONTENT to browse, not this one).
//
// Fields:
//   - sidebarBgColor    -> dashboardSidebar.tsx's sidebar background
//   - activeNavBgColor / activeNavTextColor
//                       -> the currently-selected sidebar link's colors
//   - promoCardBgColor  -> the "ORDER NOW" card background
//   - accentColor       -> mobileBottomNav.tsx's center button background
//
// Deliberately NOT included: top bar / footer bg+text colors — those already
// read from the existing "navbar"/"footer" site-config sections and must not
// be duplicated here. Sidebar nav item labels/icons/hrefs and all per-page
// dashboard content are out of scope for this component — chrome/theme only.
//
// Per-brand real colors, cross-checked against each brand's own real
// navbar/footer/hero values:
//   - MEELZ: cream/brown — #F9F3E6 (navbar bgColor), #573121 (hero primaryColor),
//            #C4832A (compatible gold accent) — meelz-client-v2's real live values.
//   - E4F:   red/white — #b11212 (navbar/footer/hero primary red), #ffffff.
//   - CK:    purple/yellow — #46298A (footer bgColor / hero primaryColor),
//            #9D91B9 (navbar/hero bgColor), #fbeb5a (navbar CTA yellow accent).
//
// `bgColor`/`textColor`-style short naming used throughout, matching
// navbar/footer's convention (this is chrome, not content) rather than
// hero/faq/ourAdvantage's `backgroundColor`/`primaryColor` convention.

const BRAND_DEFAULT_PROPS_BY_CODE = {
    MEELZ: {
        sidebarBgColor:      "#F9F3E6",
        sidebarTextColor:    "#573121",
        activeNavBgColor:    "#573121",
        activeNavTextColor:  "#ffffff",
        promoCardBgColor:    "#EE86001A",
        promoCardTextColor:  "#573121",
        accentColor:         "#C4832A",
    },
    E4F: {
        // Sidebar background is the real E4F brand red — matches this
        // repo's own original, non-admin-configurable hardcoded fallback
        // (dashboard-sidebar.tsx's "bg-primary" default when no themeProps
        // is active at all). The active item inverts to a white background
        // with red text (also matching that same original fallback's
        // "bg-white text-primary") so it still stands out against the now-
        // red sidebar — leaving activeNavBgColor at the same red as the
        // sidebar itself would make the active tab invisible against it.
        sidebarBgColor:      "#b11212",
        sidebarTextColor:    "#ffffff",
        activeNavBgColor:    "#ffffff",
        activeNavTextColor:  "#b11212",
        promoCardBgColor:    "#ffffff33",
        promoCardTextColor:  "#ffffff",
        accentColor:         "#b11212",
    },
    CK: {
        // sidebarBgColor was previously "#F9F3E6" — an exact copy-paste of
        // MEELZ's own value above, never actually corrected to CK's real
        // color. CK's real dashboard sidebar (components/dashboard/
        // dashboard-sidebar.tsx's own hardcoded fallback) is #9D91B9,
        // matching CK's navbar/hero background — confirmed bug, fixed here.
        sidebarBgColor:      "#9D91B9",
        sidebarTextColor:    "#ffffff",
        activeNavBgColor:    "#46298A",
        activeNavTextColor:  "#ffffff",
        promoCardBgColor:    "#fbeb5a33",
        promoCardTextColor:  "#46298A",
        accentColor:         "#9D91B9",
    },
};

const brandCode = process.env.REACT_APP_PROJECT_CODE || "CK";

// DEMO (and any future unmapped brand) falls back to MEELZ's colors —
// same fallback every other unmapped-brand component already has via
// componentRegistry.js's own DEFAULT_LAYOUT_BY_BRAND, not a new gap.
export const DASHBOARD_THEME_DEFAULT_PROPS =
    BRAND_DEFAULT_PROPS_BY_CODE[brandCode] || BRAND_DEFAULT_PROPS_BY_CODE.MEELZ;

// Every brand's raw, never-translated color defaults — not just the active
// session's. Needed by componentRegistry.js's never-edited backfill check:
// under the current shared testing DB, a saved color that's untouched from
// a DIFFERENT brand's perspective (e.g. CK's admin never picked a color,
// so the shared row still holds CK's raw "#46298A") must still be
// recognized as "never genuinely edited" and backfilled to THIS session's
// real brand color — comparing only against the active brand's own raw
// default misses this and permanently freezes the wrong brand's color in
// place, surviving even a Reset+Publish (confirmed bug).
export const DASHBOARD_THEME_ALL_BRAND_DEFAULTS = BRAND_DEFAULT_PROPS_BY_CODE;

export const DASHBOARD_THEME_LAYOUTS = {
    default: { label: "Client Dashboard", defaultProps: DASHBOARD_THEME_DEFAULT_PROPS },
};

export const DASHBOARD_THEME_SCHEMA = [
    { key: "sidebarBgColor",     label: "Sidebar background",           type: "color" },
    { key: "sidebarTextColor",   label: "Sidebar text color",           type: "color" },
    { key: "activeNavBgColor",   label: "Active menu item background",  type: "color" },
    { key: "activeNavTextColor", label: "Active menu item text",        type: "color" },
    { key: "promoCardBgColor",   label: "\"Order Now\" card background", type: "color" },
    { key: "promoCardTextColor", label: "\"Order Now\" card text color", type: "color" },
    { key: "accentColor",        label: "Mobile accent color",          type: "color" },
];
